/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import 'regenerator-runtime'
import './LogrocketInit'
import '../index.css'
import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { httpsCallable } from 'firebase/functions'
import { logEvent } from 'firebase/analytics'
import { SpeakableProvider, createFsClient } from '@speakableio/core'

import App from './App'
import { AuthProvider } from './hooks/useAuth'
import store from './Redux/store'
import audioClips from './assets/audio'
import Splash from './components/Splash/Splash'
import mui_theme from './assets/mui_theme/theme'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { UtilitiesProvider } from './hooks/useUtilities'
import { LocationHistoryProvider } from './context/LocationHistoryProvider'

// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   'pdfjs-dist/build/pdf.worker.min.js',
//   import.meta.url,
// ).toString()

import 'typeface-poppins'
import 'typeface-pacifico'
import '@splidejs/react-splide/css'

import { analytics, db, functions } from './firebase'
import { useUser } from './hooks/useUser'
import usePermissions from './hooks/usePermissions'
import { usePermissionsV2 } from './hooks/usePermissionsV2'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error
// @ts-ignore
window.audio = {
  ...audioClips,
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 3000000,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <LocationHistoryProvider>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ThemeProvider theme={mui_theme}>
              <UtilitiesProvider>
                <Suspense fallback={<Splash />}>
                  <BrowserRouter>
                    <WrapperSpeakableProvider>
                      <CssBaseline />
                      <App />
                    </WrapperSpeakableProvider>
                  </BrowserRouter>
                </Suspense>
              </UtilitiesProvider>
            </ThemeProvider>
          </AuthProvider>
          <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
        </QueryClientProvider>
      </Provider>
    </LocationHistoryProvider>
  </React.StrictMode>,
)

function WrapperSpeakableProvider({ children }: { children: React.ReactNode }) {
  const { auth, profile } = useUser()

  const { permissionsObject } = usePermissions()
  const permissionsv2 = usePermissionsV2()

  const fsClient = createFsClient({
    db: db,
    httpsCallable: functionName => {
      return httpsCallable(functions, functionName)
    },
    logEvent: (nameLog, data) => {
      logEvent(analytics, nameLog, data as Record<string, any>)
    },
  })

  return (
    <SpeakableProvider
      fsClient={fsClient}
      permissions={{
        ...permissionsObject,
        permissions: permissionsv2.permissions,
        hasStudentPortfolios: permissionsv2.hasStudentPortfolios,
      }}
      queryClient={queryClient}
      user={{
        auth,
        profile: {
          displayName: profile?.displayName,
          googleClassroomUserId: profile.googleClassroomUserId ?? '',
          image: {
            url: profile.image?.url ?? '',
            path: profile.image?.path ?? '',
          },
          isStudent: profile.isStudent,
          isTeacher: profile.isTeacher,
          roles: profile.roles,
        },
        lti: {},
        roles: [],
      }}
    >
      {children}
    </SpeakableProvider>
  )
}
